import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Card } from 'antd';
import { useQuery } from '@apollo/client';
import { queries } from '../../../graphql/graphql';
import {
  DirectoriesValuesByIdsResponse,
  DirectoryResponse,
  DirectoryValue,
} from '../../../types/directory';

import { Loader } from '../../UI/Loader';
import { BaseOptionType } from 'antd/es/select';

interface AvailableArea {
  states: string[];
  counties: string[];
  cities: string[];
}

interface AvailableAreaSelectorProps {
  value?: AvailableArea;
  onChange?: (value: AvailableArea) => void;
  disabled?: boolean;
}
// TODO: refactoring needed, redundant logic and code
const AvailableAreaSelector: React.FC<AvailableAreaSelectorProps> = ({
  value = { states: [], counties: [], cities: [] },
  onChange,
  disabled = false,
}) => {
  const [availableStates, setAvailableStates] = useState<string[]>(value.states || []);
  const [availableCounties, setAvailableCounties] = useState<string[]>(value.counties || []);
  const [availableCities, setAvailableCities] = useState<string[]>(value.cities || []);
  const [countyParentMap, setCountyParentMap] = useState<Record<string, string>>({});
  const [cityParentMap, setCityParentMap] = useState<Record<string, string>>({});

  const { loading: statesLoading, data: statesData } = useQuery<DirectoryResponse>(
    queries.directories.DIRECTORY,
    {
      variables: { slug: 'us-states' },
    }
  );

  const { loading: countiesLoading, data: countiesData } = useQuery<DirectoriesValuesByIdsResponse>(
    queries.directories.DIRECTORIES_VALUES_BY_IDS,
    {
      variables: { ids: availableStates },
      skip: availableStates.length === 0,
    }
  );

  const { loading: citiesLoading, data: citiesData } = useQuery<DirectoriesValuesByIdsResponse>(
    queries.directories.DIRECTORIES_VALUES_BY_IDS,
    {
      variables: { ids: availableCounties },
      skip: availableCounties.length === 0,
    }
  );

  const validateStates = useCallback(() => {
    if (!statesLoading && statesData?.directory) {
      const validStateIds = statesData.directory.values.map(item => item.directory || item.id);
      const filteredStates = availableStates.filter(stateId => validStateIds.includes(stateId));

      if (filteredStates.length !== availableStates.length) {
        setAvailableStates(filteredStates);
        onChange?.({
          ...value,
          states: filteredStates,
          counties: [],
          cities: [],
        });
      }
    }
  }, [statesLoading, statesData, availableStates, value, onChange]);

  const validateCounties = useCallback(() => {
    if (!countiesLoading && countiesData?.directoriesValuesByIds) {
      const validCountyIds = countiesData.directoriesValuesByIds.map(
        item => item.directory || item.id
      );
      const filteredCounties = availableCounties.filter(countyId =>
        validCountyIds.includes(countyId)
      );

      if (filteredCounties.length !== availableCounties.length) {
        setAvailableCounties(filteredCounties);
        onChange?.({ ...value, counties: filteredCounties });
      }
    }
  }, [countiesLoading, countiesData, availableCounties, value, onChange]);

  const validateCities = useCallback(() => {
    if (!citiesLoading && citiesData?.directoriesValuesByIds) {
      const validCityIds = citiesData.directoriesValuesByIds.map(item => item.directory || item.id);
      const filteredCities = availableCities.filter(cityId => validCityIds.includes(cityId));

      if (filteredCities.length !== availableCities.length) {
        setAvailableCities(filteredCities);
        onChange?.({ ...value, cities: filteredCities });
      }
    }
  }, [citiesLoading, citiesData, availableCities, value, onChange]);

  useEffect(() => {
    validateStates();
    validateCounties();
    validateCities();
  }, [validateStates, validateCounties, validateCities]);

  useEffect(() => {
    if (value) {
      setAvailableStates(value.states || []);
      setAvailableCounties(value.counties || []);
      setAvailableCities(value.cities || []);
    }
  }, [value]);

  const getOptions = (data?: DirectoryValue[]): BaseOptionType[] => {
    if (!data) {
      return [];
    }

    return data.map(item => ({
      label: item.name,
      value: item.directory || item.id,
      parentId: item.parentId,
    }));
  };

  const handleStatesChange = (value: string[]) => {
    const removedStates = availableStates.filter(stateId => !value.includes(stateId));
    setAvailableStates(value);

    let updatedCounties = availableCounties;
    if (removedStates.length) {
      updatedCounties = availableCounties.filter(countyId => {
        const parentState = countyParentMap[countyId];
        return !parentState || !removedStates.includes(parentState);
      });
      if (updatedCounties.length !== availableCounties.length) {
        setAvailableCounties(updatedCounties);
      }
    }

    let updatedCities = availableCities;
    if (updatedCounties.length !== availableCounties.length) {
      updatedCities = availableCities.filter(cityId => {
        const parentCounty = cityParentMap[cityId];
        return !parentCounty || updatedCounties.includes(parentCounty);
      });
      if (updatedCities.length !== availableCities.length) {
        setAvailableCities(updatedCities);
      }
    }

    onChange?.({
      states: value,
      counties: updatedCounties,
      cities: updatedCities,
    });
  };

  const handleCountiesChange = (value: string[]) => {
    const removedCounties = availableCounties.filter(countyId => !value.includes(countyId));
    setAvailableCounties(value);

    let updatedCities = availableCities;
    if (removedCounties.length) {
      updatedCities = availableCities.filter(cityId => {
        const parentCounty = cityParentMap[cityId];
        return !parentCounty || !removedCounties.includes(parentCounty);
      });
      if (updatedCities.length !== availableCities.length) {
        setAvailableCities(updatedCities);
      }
    }

    onChange?.({
      states: availableStates,
      counties: value,
      cities: updatedCities,
    });
  };

  const handleCitiesChange = (value: string[]) => {
    setAvailableCities(value);
    onChange?.({
      states: availableStates,
      counties: availableCounties,
      cities: value,
    });
  };

  useEffect(() => {
    if (countiesData?.directoriesValuesByIds) {
      const map: Record<string, string> = {};
      countiesData.directoriesValuesByIds.forEach(item => {
        const valueId = item.directory || item.id;
        if (valueId) {
          map[valueId] = item.parentId || '';
        }
      });
      setCountyParentMap(map);
    }
  }, [countiesData]);

  useEffect(() => {
    if (citiesData?.directoriesValuesByIds) {
      const map: Record<string, string> = {};
      citiesData.directoriesValuesByIds.forEach(item => {
        const valueId = item.directory || item.id;
        if (valueId) {
          map[valueId] = item.parentId || '';
        }
      });
      setCityParentMap(map);
    }
  }, [citiesData]);

  if (statesLoading) {
    return <Loader />;
  }

  return (
    <Card title="Available Area">
      <div style={{ marginBottom: '16px' }}>
        <Select
          disabled={disabled}
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Available States"
          onChange={handleStatesChange}
          options={statesData?.directory ? getOptions(statesData.directory.values) : []}
          value={availableStates}
        />
      </div>

      {availableStates.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          {countiesLoading ? (
            <Spin />
          ) : (
            <Select
              disabled={disabled}
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Available Counties"
              onChange={handleCountiesChange}
              options={
                countiesData?.directoriesValuesByIds
                  ? getOptions(countiesData.directoriesValuesByIds)
                  : []
              }
              value={availableCounties}
            />
          )}
        </div>
      )}

      {availableCounties.length > 0 && (
        <div>
          {citiesLoading ? (
            <Spin />
          ) : (
            <Select
              disabled={disabled}
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Available Cities"
              onChange={handleCitiesChange}
              options={
                citiesData?.directoriesValuesByIds
                  ? getOptions(citiesData.directoriesValuesByIds)
                  : []
              }
              value={availableCities}
            />
          )}
        </div>
      )}
    </Card>
  );
};

export default AvailableAreaSelector;
